/**
 * The mark, and the product name.
 *
 * The mark is a strip of whelping collars — the coloured rings a breeder ties
 * on newborns so five three-day-old puppies can be told apart before any of them
 * has a name. It is the most characteristic object in this product's world and
 * it is already in the data model (`Offspring.markerColor`), so the sign-in
 * screen opens with the app's own vocabulary rather than a generic glyph.
 *
 * It is the one loud thing in the interface. Everything else stays quiet on
 * purpose: real marker colours are arbitrary and bright, and a chrome that
 * competes with them makes the list unreadable.
 */
import { cn } from '@/lib/cn.ts';

/**
 * Drawn from the `--color-ribbon-*` tokens. Six, not the full seven, and
 * overlapping tightly: laid out evenly across the full spectrum they stop
 * reading as a handful of collars and start reading as a colour wheel, which is
 * a decoration rather than an object from this world.
 */
const COLLARS = [
  'border-ribbon-red',
  'border-ribbon-orange',
  'border-ribbon-green',
  'border-ribbon-blue',
  'border-ribbon-violet',
  'border-ribbon-pink',
] as const;

export const CollarStrip = ({ className }: { className?: string }) => (
  <div className={cn('flex', className)} aria-hidden>
    {COLLARS.map((collar, index) => (
      <span
        key={collar}
        className={cn('size-6 rounded-full border-[3px] bg-page', collar, index > 0 && '-ml-3')}
      />
    ))}
  </div>
);

/**
 * The app icon, inline.
 *
 * The same paw on the same rounded plate as `public/icon.svg` and the installed
 * app's launcher icon, redrawn as a component so the header can show the mark
 * the user tapped on their home screen. Inline rather than `<img src="/icon.svg">`
 * for the same reason the Google mark on the sign-in screen is inline: it is
 * chrome that must paint on the first frame, offline, with no request.
 *
 * The two fills are theme tokens, not the hex pairs in the manifest — `surface`
 * is the white of the paper this is printed on, and the plate is the brand fill.
 * The manifest and index.html keep their literals because neither can read a CSS
 * variable; this can, so it does.
 */
export const Mark = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 512 512" className={cn('shrink-0', className)} aria-hidden focusable="false">
    <rect width="512" height="512" rx="112" className="fill-brand-600" />
    <g className="fill-surface">
      <ellipse cx="182" cy="176" rx="46" ry="58" />
      <ellipse cx="330" cy="176" rx="46" ry="58" />
      <ellipse cx="118" cy="286" rx="44" ry="52" transform="rotate(-20 118 286)" />
      <ellipse cx="394" cy="286" rx="44" ry="52" transform="rotate(20 394 286)" />
      <path d="M256 250c58 0 106 44 106 92 0 34-26 54-62 54-20 0-30-8-44-8s-24 8-44 8c-36 0-62-20-62-54 0-48 48-92 106-92z" />
    </g>
    <path
      className="fill-brand-600"
      transform="translate(198 302) scale(1.15)"
      d="M50,88 C15,65 0,45 0,25 C0,10 12,0 25,0 C35,0 45,7 50,18 C55,7 65,0 75,0 C88,0 100,10 100,25 C100,45 85,65 50,88 Z"
    />
  </svg>
);

/**
 * "Puppy Doctor" is the product's name and stays out of the phrase tree: a name
 * is not copy, and translating it would give the same app two identities.
 */
export const Wordmark = ({ className }: { className?: string }) => (
  <span className={cn('font-bold tracking-[-0.02em] text-brand-ink', className)}>Puppy Doctor</span>
);
